"use client";

import { RiPlayFill, RiStopFill } from "@remixicon/react";
import { cn } from "@workspace/ui/lib/utils";
import { memo, useCallback, useEffect, useState } from "react";
import { getArtistTopTracksAction } from "@/lib/actions/spotify/get-top-tracks.action";
import { spotifyAudioManager } from "@/lib/audio/spotify-audio-manager";

interface SpotifyPlayButtonProps {
	spotifyId: string | null | undefined;
	artistName?: string;
	className?: string;
}

/**
 * Spotify play button that plays a 30-second audio preview
 */
export const SpotifyPlayButton = memo(function SpotifyPlayButton({
	spotifyId,
	artistName,
	className,
}: SpotifyPlayButtonProps) {
	const [isPlaying, setIsPlaying] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);
	const [noPreview, setNoPreview] = useState(false);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (spotifyId && spotifyAudioManager.isPlaying(spotifyId)) {
				spotifyAudioManager.stop();
			}
		};
	}, [spotifyId]);

	const fetchTopTrack = useCallback(async () => {
		if (!spotifyId || previewUrl || isLoading) return previewUrl;

		setIsLoading(true);
		try {
			const result = await getArtistTopTracksAction({
				spotifyId,
				artistName,
				limit: 1,
			});

			if (result.success && result.data?.[0]?.previewUrl) {
				setPreviewUrl(result.data[0].previewUrl);
				return result.data[0].previewUrl;
			}
			setNoPreview(true);
			return null;
		} catch {
			setNoPreview(true);
			return null;
		} finally {
			setIsLoading(false);
		}
	}, [spotifyId, artistName, previewUrl, isLoading]);

	const handleClick = useCallback(
		async (e: React.MouseEvent) => {
			e.stopPropagation();
			e.preventDefault();

			if (isLoading || !spotifyId) return;

			// If playing, stop
			if (isPlaying) {
				spotifyAudioManager.stop();
				return;
			}

			// Fetch and play
			const url = previewUrl || (await fetchTopTrack());
			if (!url) return;

			spotifyAudioManager.play(spotifyId, url, setIsPlaying);
		},
		[isPlaying, isLoading, previewUrl, fetchTopTrack, spotifyId],
	);

	// Don't render if no spotify ID or no preview available
	if (!spotifyId || noPreview) {
		return null;
	}

	return (
		<button
			type="button"
			onClick={handleClick}
			disabled={isLoading}
			className={cn(
				"flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#1DB954] text-white shadow-lg transition-all duration-200",
				"hover:scale-105 hover:bg-[#1ed760]",
				"active:scale-95",
				"disabled:cursor-not-allowed",
				className,
			)}
			title={
				isPlaying
					? `Stop ${artistName || "track"}`
					: `Preview ${artistName || "artist"}`
			}
			aria-label={
				isPlaying
					? `Stop ${artistName || "track"}`
					: `Preview ${artistName || "artist"}`
			}
		>
			{isLoading ? (
				<div className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
			) : isPlaying ? (
				<RiStopFill className="h-5 w-5" />
			) : (
				<RiPlayFill className="h-5 w-5" />
			)}
		</button>
	);
});

SpotifyPlayButton.displayName = "SpotifyPlayButton";
